import React, { useEffect, useRef, useState } from 'react';
import { Chip, Popover, PopoverContent, PopoverTrigger } from '@heroui/react';
import { Typography } from '../../../../components';
import { RefShareOffer } from '../../../../api/services';
import ProhibitedSourcesTooltip from './ProhibitedSourcesTooltip';
import BlockedCountriesTooltip from './BlockedCountriesTooltip';

interface RevshareDetailsCellProps {
  offer: RefShareOffer;
}

const RevshareDetailsCell: React.FC<RevshareDetailsCellProps> = ({ offer }) => {
  const [isBlockedCountriesOpen, setIsBlockedCountriesOpen] = useState(false);
  const [isProhibitedSourcesOpen, setIsProhibitedSourcesOpen] = useState(false);
  const blockedCountriesTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prohibitedSourcesTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (blockedCountriesTimeoutRef.current) {
        clearTimeout(blockedCountriesTimeoutRef.current);
      }
      if (prohibitedSourcesTimeoutRef.current) {
        clearTimeout(prohibitedSourcesTimeoutRef.current);
      }
    };
  }, []);

  const openBlockedCountries = () => {
    if (blockedCountriesTimeoutRef.current) {
      clearTimeout(blockedCountriesTimeoutRef.current);
      blockedCountriesTimeoutRef.current = null;
    }
    setIsBlockedCountriesOpen(true);
  };

  const scheduleBlockedCountriesClose = () => {
    if (blockedCountriesTimeoutRef.current) {
      clearTimeout(blockedCountriesTimeoutRef.current);
    }
    blockedCountriesTimeoutRef.current = setTimeout(() => {
      setIsBlockedCountriesOpen(false);
    }, 120);
  };

  const openProhibitedSources = () => {
    if (prohibitedSourcesTimeoutRef.current) {
      clearTimeout(prohibitedSourcesTimeoutRef.current);
      prohibitedSourcesTimeoutRef.current = null;
    }
    setIsProhibitedSourcesOpen(true);
  };

  const scheduleProhibitedSourcesClose = () => {
    if (prohibitedSourcesTimeoutRef.current) {
      clearTimeout(prohibitedSourcesTimeoutRef.current);
    }
    prohibitedSourcesTimeoutRef.current = setTimeout(() => {
      setIsProhibitedSourcesOpen(false);
    }, 120);
  };

  const commission = offer.offer?.commission ?? 0;
  const prohibitedSourcesList = offer.offer?.prohibited_sources || [];
  const blockedCountriesList = offer.of_data?.blocked_countries || [];
  const offerShortId = offer?._id ? offer._id.slice(-4).toUpperCase() : '----';
  const offerType = offer.type || 'free';

  return (
    <div className="flex flex-col gap-[4px]">
      <div className="flex items-center gap-[8px]">
        <Typography variant="body3" className="text-[#11181c] font-semibold text-[12px] leading-[16px]">
          По ссылке
        </Typography>
        {offerType === 'free trial' && (
          <Chip className="h-[20px] px-[6px] bg-[#006FEE]/10 text-[#006FEE] text-[11px]">
            Free-trial
          </Chip>
        )}
        {offerType === 'free' && (
          <Chip className="h-[20px] px-[6px] bg-[#17C964]/10 text-[#17C964] text-[11px]">
            Free
          </Chip>
        )}
      </div>

      <div className="flex items-center gap-[4px]">
        <Typography variant="body3" className="text-zinc-500 text-[12px] leading-[16px]">
          Комиссия:
        </Typography>
        <Typography variant="body3" className="text-[#11181c] font-semibold text-[14px] leading-[16px]">
          {commission}%
        </Typography>
      </div>

      {/* Prohibited sources */}
      {prohibitedSourcesList.length > 0 ? (
        <Popover
          placement="bottom-start"
          offset={12}
          showArrow
          isOpen={isProhibitedSourcesOpen}
          onOpenChange={setIsProhibitedSourcesOpen}
          shouldCloseOnBlur
          isDismissable
        >
          <PopoverTrigger>
            <div
              className="flex items-center gap-[4px] cursor-pointer"
              onMouseEnter={openProhibitedSources}
              onMouseLeave={scheduleProhibitedSourcesClose}
            >
              <Typography variant="body3" className="text-zinc-500 text-[12px] leading-[16px]">
                Заблокированные ресурсы:
              </Typography>
              <Typography variant="body3" className="text-[#11181c] font-semibold text-[14px] leading-[16px]">
                {prohibitedSourcesList.length}
              </Typography>
            </div>
          </PopoverTrigger>
          <PopoverContent
            className="p-0 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl"
            onMouseEnter={openProhibitedSources}
            onMouseLeave={scheduleProhibitedSourcesClose}
          >
            <ProhibitedSourcesTooltip
              sources={prohibitedSourcesList}
              offerShortId={offerShortId}
              modelUsername={offer.of_user_name}
            />
          </PopoverContent>
        </Popover>
      ) : (
        <div className="flex items-center gap-[4px]">
          <Typography variant="body3" className="text-zinc-500 text-[12px] leading-[16px]">
            Заблокированные ресурсы:
          </Typography>
          <Typography variant="body3" className="text-[#11181c] font-semibold text-[14px] leading-[16px]">
            0
          </Typography>
        </div>
      )}

      {/* Blocked countries */}
      {blockedCountriesList.length > 0 ? (
        <Popover
          placement="bottom-start"
          offset={12}
          showArrow
          isOpen={isBlockedCountriesOpen}
          onOpenChange={setIsBlockedCountriesOpen}
          shouldCloseOnBlur
          isDismissable
        >
          <PopoverTrigger>
            <div
              className="flex items-center gap-[4px] cursor-pointer"
              onMouseEnter={openBlockedCountries}
              onMouseLeave={scheduleBlockedCountriesClose}
            >
              <Typography variant="body3" className="text-zinc-500 text-[12px] leading-[16px]">
                Заблокированные страны:
              </Typography>
              <Typography variant="body3" className="text-[#11181c] font-semibold text-[14px] leading-[16px]">
                {blockedCountriesList.length}
              </Typography>
            </div>
          </PopoverTrigger>
          <PopoverContent
            className="p-0 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl"
            onMouseEnter={openBlockedCountries}
            onMouseLeave={scheduleBlockedCountriesClose}
          >
            <BlockedCountriesTooltip
              countries={blockedCountriesList}
              offerShortId={offerShortId}
              modelUsername={offer.of_user_name}
            />
          </PopoverContent>
        </Popover>
      ) : (
        <div className="flex items-center gap-[4px]">
          <Typography variant="body3" className="text-zinc-500 text-[12px] leading-[16px]">
            Заблокированные страны:
          </Typography>
          <Typography variant="body3" className="text-[#11181c] font-semibold text-[14px] leading-[16px]">
            0
          </Typography>
        </div>
      )}
    </div>
  );
};

export default RevshareDetailsCell;
